import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { Minus, Plus } from "lucide-react";
import ModalBasicLayout from "./ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import useChangeSeats, {
  seatsChangeErrorMessage,
} from "../../hooks/useChangeSeats";
import { startPreviewSeatsChange } from "../../actions/featureAddons";
import { useAgencySeats } from "../../hooks/useAgencyFeatureAddons";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import {
  BUTTON_CANCEL,
  EXTRA_SEATS_ACTIVE_MEMBERS_BLOCK,
  EXTRA_SEATS_ACTIVE_MEMBERS_BLOCK_ONE,
  EXTRA_SEATS_AMOUNT_PER_MONTH,
  EXTRA_SEATS_CONFIRM,
  EXTRA_SEATS_DECREASE,
  EXTRA_SEATS_DUE_TODAY_LABEL,
  EXTRA_SEATS_INCREASE,
  EXTRA_SEATS_KEEP_UNTIL_CYCLE,
  EXTRA_SEATS_KEEP_UNTIL_CYCLE_ONE,
  EXTRA_SEATS_MODAL_SUBTITLE,
  EXTRA_SEATS_MODAL_SUBTITLE_NO_PRICE,
  EXTRA_SEATS_MODAL_TITLE,
  EXTRA_SEATS_MONTHLY_TOTAL,
  EXTRA_SEATS_NEXT_TOTAL_FROM,
  EXTRA_SEATS_NEXT_TOTAL_MONTHLY,
  EXTRA_SEATS_NO_CHARGE_NOTE,
  EXTRA_SEATS_QUANTITY_LABEL,
  EXTRA_SEATS_REDUCE_BLOCKED,
  EXTRA_SEATS_REDUCE_BLOCKED_ONE,
  SEATS_NOT_ELIGIBLE,
  SEATS_PRICE_NOT_CONFIGURED,
  SEATS_UNPAID,
  USERS_TITLE,
} from "../../i18n/keysTranslations";
import {
  SEATS_QUOTE_NOTE,
  SEATS_QUOTE_ROW,
  SEATS_CHANGE_OUTCOME,
  buildSeatsChangeQuote,
  canConfirmSeatsChange,
  formatSeatAmount,
  isSeatsIncreaseLocked,
  resolveSeatsModalQuantity,
  seatsBillingQuantity,
  seatsQuantityMax,
  seatsQuantityMin,
} from "../../utils/extraSeats";

const PREVIEW_DELAY_MS = 300;

const stepperButtonSx = {
  width: 36,
  height: 36,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  color: "text.primary",
};

const moneyOrSkeleton = ({ amount, currency, locale, perMonth, t }) => {
  if (amount == null || amount === "") {
    return <Skeleton width={72} height={20} />;
  }
  const formatted = formatSeatAmount({ amount, currency, locale });
  if (!perMonth) return formatted;
  return t(EXTRA_SEATS_AMOUNT_PER_MONTH, { amount: formatted });
};

const quoteRow = (quote, type) => quote.rows.find((row) => row.type === type);

const QuoteHighlight = ({ label, amount }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 2,
      py: 0.75,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
      {amount}
    </Typography>
  </Box>
);

QuoteHighlight.propTypes = {
  label: PropTypes.node.isRequired,
  amount: PropTypes.node.isRequired,
};

const SeatsQuoteSummary = ({ quote, locale, t }) => {
  const currency = quote.currency;
  const due =
    quoteRow(quote, SEATS_QUOTE_ROW.DUE_TODAY) ||
    quoteRow(quote, SEATS_QUOTE_ROW.PRORATION);
  const next =
    quoteRow(quote, SEATS_QUOTE_ROW.NEXT_FROM) ||
    quoteRow(quote, SEATS_QUOTE_ROW.NEXT_MONTHLY);
  const monthly = quoteRow(quote, SEATS_QUOTE_ROW.MONTHLY_TOTAL);

  if (!due && !next) {
    return (
      <QuoteHighlight
        label={t(EXTRA_SEATS_MONTHLY_TOTAL)}
        amount={moneyOrSkeleton({
          amount: monthly?.amount,
          currency,
          locale,
          perMonth: true,
          t,
        })}
      />
    );
  }

  const renewAt = due?.periodEnd ?? next?.date ?? null;
  const nextLabel = renewAt
    ? t(EXTRA_SEATS_NEXT_TOTAL_FROM, { date: new Date(renewAt) })
    : t(EXTRA_SEATS_NEXT_TOTAL_MONTHLY);
  let todayCaption = "";
  if (quote.note === SEATS_QUOTE_NOTE.KEEP) {
    todayCaption =
      quote.current === 1
        ? t(EXTRA_SEATS_KEEP_UNTIL_CYCLE_ONE)
        : t(EXTRA_SEATS_KEEP_UNTIL_CYCLE, { count: quote.current });
  } else if (quote.note === SEATS_QUOTE_NOTE.FREE) {
    todayCaption = t(EXTRA_SEATS_NO_CHARGE_NOTE);
  }

  return (
    <Box>
      <QuoteHighlight
        label={t(EXTRA_SEATS_DUE_TODAY_LABEL)}
        amount={moneyOrSkeleton({
          amount: due?.amount,
          currency,
          locale,
          t,
        })}
      />
      <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />
      <QuoteHighlight
        label={nextLabel}
        amount={moneyOrSkeleton({
          amount: next?.amount,
          currency,
          locale,
          perMonth: true,
          t,
        })}
      />
      {todayCaption ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.25 }}
        >
          {todayCaption}
        </Typography>
      ) : null}
    </Box>
  );
};

SeatsQuoteSummary.propTypes = {
  quote: PropTypes.shape({
    currency: PropTypes.string,
    current: PropTypes.number,
    note: PropTypes.string,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  locale: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

const reductionMessage = ({ block, t }) => {
  if (!block) return "";
  const section = t(USERS_TITLE);
  if (block.usedSeats == null) {
    return block.removeCount === 1
      ? t(EXTRA_SEATS_REDUCE_BLOCKED_ONE, { section })
      : t(EXTRA_SEATS_REDUCE_BLOCKED, {
          count: block.removeCount,
          section,
        });
  }
  if (block.removeCount === 1) {
    return t(EXTRA_SEATS_ACTIVE_MEMBERS_BLOCK_ONE, {
      used: block.usedSeats,
      section,
    });
  }
  return t(EXTRA_SEATS_ACTIVE_MEMBERS_BLOCK, {
    used: block.usedSeats,
    count: block.removeCount,
    section,
  });
};

const ModalManageExtraSeats = ({
  modalOpen = false,
  onCloseModal = () => {},
  onCompleted = () => {},
  onQuantityChanged = () => {},
  seatsStatus = null,
  planUsersFallback = null,
  purpose = "manage",
  draftUser = null,
  returnTo,
}) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const locale = i18n.language;
  const touchedRef = useRef(false);
  const wasOpenRef = useRef(false);
  const [draftQuantity, setDraftQuantity] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { seatsStatus: liveStatus, isLoading: liveLoading } = useAgencySeats({
    enabled: modalOpen,
  });

  const resolvedStatus = useMemo(
    () => ({ ...(seatsStatus || {}), ...(liveStatus || {}) }),
    [liveStatus, seatsStatus]
  );
  const currentQuantity = seatsBillingQuantity(resolvedStatus);
  const quantityMax = seatsQuantityMax(resolvedStatus);
  const increaseLocked = isSeatsIncreaseLocked(resolvedStatus);
  const isBelowMin =
    draftQuantity != null && draftQuantity < seatsQuantityMin(resolvedStatus);
  const showUnpaid =
    resolvedStatus?.billingStatus === "unpaid" &&
    (draftQuantity ?? 0) >= currentQuantity;
  const showRefusedIncrease =
    !showUnpaid &&
    resolvedStatus?.canBuy === false &&
    (draftQuantity ?? 0) > currentQuantity;
  const skipPreview =
    draftQuantity == null ||
    draftQuantity === currentQuantity ||
    isBelowMin ||
    (draftQuantity > currentQuantity && increaseLocked);

  const onServerQuantityChanged = (interpreted) => {
    touchedRef.current = false;
    onQuantityChanged(interpreted);
  };

  const { submitSeatsChange } = useChangeSeats({
    returnTo,
    onCompleted,
    onQuantityChanged: onServerQuantityChanged,
  });

  useEffect(() => {
    if (modalOpen) {
      wasOpenRef.current = true;
      return undefined;
    }
    if (!wasOpenRef.current) return undefined;
    wasOpenRef.current = false;
    touchedRef.current = false;
    setDraftQuantity(null);
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setSubmitting(false);
    return undefined;
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen || touchedRef.current) return;
    if (purpose === "add" && !liveStatus && !seatsStatus) return;
    const basis = liveStatus || seatsStatus;
    setDraftQuantity(
      resolveSeatsModalQuantity({ seatsStatus: basis, purpose })
    );
  }, [liveStatus, modalOpen, purpose, seatsStatus]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    if (skipPreview) {
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreview(null);
    setPreviewError(null);
    const timer = setTimeout(() => {
      dispatch(startPreviewSeatsChange({ targetQuantity: draftQuantity })).then(
        (result) => {
          if (cancelled) return;
          setPreviewLoading(false);
          if (result?.ok) {
            setPreview(result.data || null);
            setPreviewError(null);
            return;
          }
          setPreview(null);
          setPreviewError(result || { ok: false });
        }
      );
    }, PREVIEW_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatch, draftQuantity, modalOpen, skipPreview]);

  const unitPrice = Number(resolvedStatus?.unitPriceMonthly);
  const priceKnown = Number.isFinite(unitPrice);
  const waitingForPrice = !priceKnown && modalOpen && liveLoading;
  const quote = buildSeatsChangeQuote({
    currentQuantity,
    targetQuantity: draftQuantity ?? currentQuantity,
    unitPriceMonthly: resolvedStatus?.unitPriceMonthly,
    currency: resolvedStatus?.currency,
    seatsStatus: resolvedStatus,
    preview,
  });
  const canConfirm = canConfirmSeatsChange({
    currentQuantity,
    targetQuantity: draftQuantity ?? currentQuantity,
    blocked: isBelowMin,
    unpaid: showUnpaid || showRefusedIncrease,
    previewReady: Boolean(preview) && !previewLoading && !previewError,
  });

  const includedUsers = Number(resolvedStatus?.planUsers ?? planUsersFallback);
  const hasIncluded = Number.isFinite(includedUsers) && includedUsers > 0;
  const priceLabel = formatSeatAmount({
    amount: resolvedStatus?.unitPriceMonthly,
    currency: quote.currency,
    locale,
  });
  const description = !hasIncluded
    ? ""
    : priceLabel
      ? t(EXTRA_SEATS_MODAL_SUBTITLE, {
          planUsers: includedUsers,
          price: priceLabel,
        })
      : t(EXTRA_SEATS_MODAL_SUBTITLE_NO_PRICE, {
          planUsers: includedUsers,
        });

  const blockText = isBelowMin
    ? reductionMessage({ block: quote.block, t })
    : "";
  const previewErrorText =
    !blockText && !showUnpaid && !showRefusedIncrease && previewError
      ? seatsChangeErrorMessage({ result: previewError, t }).message
      : "";
  const notice =
    blockText ||
    (showUnpaid ? t(SEATS_UNPAID) : "") ||
    (showRefusedIncrease ? t(SEATS_NOT_ELIGIBLE) : "") ||
    previewErrorText;

  const onStepQuantity = (step) => {
    touchedRef.current = true;
    setDraftQuantity((current) => {
      const next = (current ?? currentQuantity) + step;
      if (next < 0) return 0;
      if (next > quantityMax) return quantityMax;
      return next;
    });
  };

  const onConfirm = async () => {
    if (!canConfirm || draftQuantity == null || submitting) return;
    setSubmitting(true);
    let keepBusy = false;
    try {
      const interpreted = await submitSeatsChange({
        targetQuantity: draftQuantity,
        expectedQuantity: currentQuantity,
        draftUser,
        purpose,
      });
      keepBusy = interpreted?.outcome === SEATS_CHANGE_OUTCOME.CHECKOUT;
    } finally {
      if (!keepBusy) setSubmitting(false);
    }
  };

  const requestClose = () => {
    if (submitting) return;
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={requestClose}
      title={t(EXTRA_SEATS_MODAL_TITLE)}
      description={description}
      variantTitle="h6"
      variantDescription="body2"
      sxTitle={{ fontWeight: 700 }}
      sxDescription={{ color: "text.secondary", mt: 0.75 }}
      ariaLabel={t(EXTRA_SEATS_MODAL_TITLE)}
      maxWidth="xs"
      fullWidth
      showCloseButton={!submitting}
    >
      {draftQuantity == null || waitingForPrice ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {t(EXTRA_SEATS_QUANTITY_LABEL)}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                aria-label={t(EXTRA_SEATS_DECREASE)}
                disabled={submitting || draftQuantity <= 0}
                onClick={() => onStepQuantity(-1)}
                sx={stepperButtonSx}
              >
                <Minus size={16} />
              </IconButton>
              <Typography
                sx={{
                  minWidth: 40,
                  textAlign: "center",
                  fontWeight: 700,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 1,
                  py: 0.75,
                }}
              >
                {draftQuantity}
              </Typography>
              <IconButton
                aria-label={t(EXTRA_SEATS_INCREASE)}
                disabled={
                  submitting || increaseLocked || draftQuantity >= quantityMax
                }
                onClick={() => onStepQuantity(1)}
                sx={stepperButtonSx}
              >
                <Plus size={16} />
              </IconButton>
            </Box>
          </Box>
          {notice ? (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 1.5 }}
              role="alert"
            >
              {notice}
            </Typography>
          ) : null}
          {isBelowMin ? null : (
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                mt: 2,
                pt: 1,
              }}
              aria-live="polite"
            >
              {priceKnown ? (
                <SeatsQuoteSummary quote={quote} locale={locale} t={t} />
              ) : (
                <Typography variant="body2" color="error" role="alert">
                  {t(SEATS_PRICE_NOT_CONFIGURED)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
      <DialogActions sx={{ p: 0 }}>
        <ContainerModalActions
          hasPadding
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            disabled: submitting,
            onClick: requestClose,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: EXTRA_SEATS_CONFIRM,
            disabled: !canConfirm || submitting || !priceKnown,
            loading: submitting,
            onClick: onConfirm,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

ModalManageExtraSeats.propTypes = {
  modalOpen: PropTypes.bool,
  onCloseModal: PropTypes.func,
  onCompleted: PropTypes.func,
  onQuantityChanged: PropTypes.func,
  seatsStatus: PropTypes.object,
  planUsersFallback: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  purpose: PropTypes.oneOf(["manage", "add"]),
  draftUser: PropTypes.object,
  returnTo: PropTypes.string,
};

export default ModalManageExtraSeats;
